import { useEffect, useState } from 'react';
import { AcceptedQuote, PeginPaymentData, Quote } from '@rsksmart/flyover-sdk';
import { Box, Typography } from '@mui/material';
import SectionContainer from '../components/UI/SectionContainer';
import SectionPaper from '../components/UI/SectionPaper';
import InfoCard from '../components/UI/InfoCard';
import JsonPaper from '../components/UI/JsonPaper';
import Timer from '../components/UI/Timer';
import useStore from '../hooks/useStore';
import QrCode from '../components/UI/QrCode';
import FlyButton from '../components/UI/FlyButton';
import FlyError from '../components/UI/FlyError';
import { downloadJson } from '../utils/download';
import { buildPeginQrPayload, weiToBtcRoundedUp } from '../utils/paymentUri';
import flyover from '../flyover';

function DepositPegIn() {
  const {
    flyover: { acceptedQuote: storeAcceptedQuote, quoteReturn },
    ui: { isDevMode },
  } = useStore();
  const acceptedQuote = storeAcceptedQuote as AcceptedQuote;
  const quote = quoteReturn[0] as Quote | undefined;

  const [paymentData, setPaymentData] = useState<PeginPaymentData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (!quote || !acceptedQuote) return;
    (async () => {
      try {
        const paymentData = await flyover.getPeginPaymentData(quote, acceptedQuote, {
          amountUnit: 'WEI',
        });
        setPaymentData({
          address: paymentData.address,
          amount: weiToBtcRoundedUp(paymentData.amount),
        });
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? `DepositPegIn: ${error.message}`
            : 'DepositPegIn: Cannot get pegin payment data',
        );
      }
    })();
  }, [quote, acceptedQuote]);

  const qrPayload = paymentData ? buildPeginQrPayload(paymentData) : '';

  function handleDownload() {
    if (!quote || !acceptedQuote) return;
    downloadJson(
      {
        quote,
        providerSignature: acceptedQuote.signature,
        userBtcTransactionHash: '',
      },
      'flyover-pegin.json',
    );
  }

  return (
    <>
      <SectionPaper heading="Deposit Funds">
        {acceptedQuote && (
          <Box
            sx={{
              display: 'flex',
              gap: '2rem',
              flexDirection: isDevMode ? 'column' : 'row',
              justifyContent: 'center',
              '@media (max-width: 1000px)': {
                flexDirection: 'row',
              },
              alignItems: isDevMode ? 'center' : 'flex-start',
              '@media (max-width: 800px)': {
                flexDirection: 'column',
                alignItems: 'center',
              },
            }}
          >
            {qrPayload && <QrCode payload={qrPayload} />}
            <SectionContainer spacing={2} sx={{ margin: 'unset' }}>
              <InfoCard
                title="Amount to deposit (BTC) = Amount to deposit + Call Fee"
                message={paymentData?.amount ?? ''}
                hasCopy
              />
              <InfoCard
                title="BTC Address"
                message={paymentData?.address ?? ''}
                hasCopy
              />
              <Timer />
              <FlyButton onClick={handleDownload}>Download</FlyButton>
            </SectionContainer>
          </Box>
        )}
        <Box
          gridArea="warning"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? '#dbdbdb' : '#3A3A3A',
            padding: '1rem',
            marginTop: '1.5rem',
            borderRadius: '1rem',
          }}
        >
          <Typography sx={{ color: '#C1423F' }}>
            <Typography sx={{ fontSize: '1.5rem', color: '#C1423F' }}>
              {' '}
              Warning!
            </Typography>
            Please ensure that you send the correct value. It's strictly
            required not to be less than calculated. You can check the
            derivated wallet balance, it must match the exact deposit amount.
            Otherwise, the money will be locked forever!
          </Typography>
        </Box>
      </SectionPaper>
      {isDevMode && (
        <SectionPaper heading="Dev Info">
          <SectionContainer spacing={2}>
            {acceptedQuote && (
              <JsonPaper title="Accepted PegIn quote" value={acceptedQuote} />
            )}
            {quote && <JsonPaper title="PegIn quote" value={quote} />}
          </SectionContainer>
        </SectionPaper>
      )}
      <FlyError errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
    </>
  );
}

export default DepositPegIn;
